import styled from "@emotion/styled";
import { AddCircle } from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { primary } from "../../enums/Colors";
import { buttonNegativeRoot, buttonPositiveRoot } from "../../enums/CssClasses";
import { useTranslation } from "react-i18next";
import { trimText, useIsMobile } from "../../../../utils/Functions";
import { nonOverflowingTitleSx } from "../../../../enums/Styles";

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  "&.MuiDialog-root .MuiDialog-paper": {
    background: theme.palette.mode === "light" ? "#ffffff" : "#202020ff",
    boxShadow: `0 10px 60px ${alpha(primary, 0.25)}`,
    border: `1px solid ${alpha(primary, 0.35)}`,
  },
}));

export default function DialogCore({
  title,
  icon = <AddCircle color="primary" />,
  isIcon = false,
  buttonSize = "medium",
  subtitle,
  buttonVariant = "contained",
  buttonTitle = "Button",
  maxWidth = "lg",
  fullWidth = true,
  color = "primary",
  fontWeight = 500,
  openButtonClass = "",
  closeButtonClass = buttonNegativeRoot,
  saveButtonClass = buttonPositiveRoot,
  dialogClass = "custom-dialog",
  showSubmit = true,
  children,
  formId, //This is needed for the submit button to work
  isLoading = false,
  disabled = false,
  buttonFullWidth,
  closeWhen,
  open: controlledOpen, // Pass open + onClose to control the dialog from outside; no trigger button is rendered then
  onClose,
}) {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  // Refs so closeWhen only reacts to its own changes, not to a new onClose on every render
  const closeRef = React.useRef();
  closeRef.current = () => (isControlled ? onClose?.() : setInternalOpen(false));

  React.useEffect(() => {
    if (closeWhen) closeRef.current();
  }, [closeWhen]);

  const handleClickOpen = () => {
    setInternalOpen(true);
  };

  const handleClose = () => {
    closeRef.current();
  };

  return (
    <React.Fragment>
      {isControlled ? null : isIcon ? (
        <IconButton onClick={handleClickOpen} title={buttonTitle} color={color}>
          {icon} 
        </IconButton>
      ) : (
        <Button
          fullWidth={buttonFullWidth}
          variant={buttonVariant}
          color={color}
          size={buttonSize}
          onClick={handleClickOpen}
        >
          {buttonTitle}
        </Button>
      )}
      <StyledDialog
        fullWidth={fullWidth}
        maxWidth={maxWidth}
        open={open}
        onClose={handleClose}
      >
        <DialogTitle marginBottom={2} fontWeight={fontWeight}>
          {title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{subtitle}</DialogContentText>
          {children}
        </DialogContent>
        <DialogActions>
          {Boolean(showSubmit) === true ? (
            <Button
              form={formId}
              disabled={isLoading || disabled}
              className={saveButtonClass}
              type="submit"
              variant="contained"
            >
              {isLoading ? <CircularProgress size={"30px"} /> : t("Save")}
            </Button>
          ) : null}
          <Button
            className={closeButtonClass}
            onClick={handleClose}
            variant="outlined"
            color="error"
          >
            {t("Close")}
          </Button>
        </DialogActions>
      </StyledDialog>
    </React.Fragment>
  );
}

export function DialogText({
  text = null,
  dialogTitle = "",
  maxLength = 500,
  title = null,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const isMobile = useIsMobile();
  if (!text) return null;

  const fullText = String(text);
  const isLong = fullText.length > maxLength - 1;
  const displayText = isLong ? trimText(fullText, maxLength) : fullText;

  function getDialogSize() {
    if (fullText.length < 100) {
      return "sm";
    } else if (fullText.length < 500) {
      return "md";
    } else if (fullText.length < 6000) {
      return "lg";
    }
    return "md";
  }

  return (
    <>
      <Box sx={{ flex: 1, textAlign: { xs: "center", md: "left" } }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            mb: 2,
            justifyContent: { xs: "center", md: "flex-start" },
            cursor: isLong ? "pointer" : "default",
          }}
          onClick={() => isLong && setOpen(true)}
        >
          <Card variant="outlined" sx={{ width: "100%", borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  {title ? (
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1,
                        color: "text.primary",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {title}
                    </Typography>
                  ) : null}
                  <Typography variant="body1" title={displayText} sx={nonOverflowingTitleSx}>
                    {displayText}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        fullScreen={isMobile}
        maxWidth={getDialogSize()}
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent dividers>
          <Typography
            fontSize={"1em"}
            lineHeight={1.45}
            sx={nonOverflowingTitleSx}
          >
            {fullText}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            color="error"
          >
            {t("Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
